import { useEffect, useRef, useState } from "react";
import {
  DOCK_ANIM_MS,
  DOCK_TRANSITION,
  computeDockTransform,
  runAfterPaint
} from "./dockAnimation";

const HIDE_DELAY_MS = DOCK_ANIM_MS + 40;
const BASE_ANIM_STYLE = { pointerEvents: "none", transformOrigin: "center center" };

const atRest = (transform, opacity) => ({
  ...BASE_ANIM_STYLE,
  transition: "none",
  transform,
  opacity
});

const animatingTo = (transform, opacity) => ({
  ...BASE_ANIM_STYLE,
  transition: DOCK_TRANSITION,
  transform,
  opacity
});

/**
 * Drives the chatbot widget's fly-to/from-navbar animation.
 *
 * Owns only the animation mechanics (transition state, frame scheduling, timers).
 * The host component supplies the domain data via callbacks:
 *  - resolveTarget(): the navbar launcher rect
 *  - resolveSource(isHidden): the widget's resting rect for the current direction
 *  - onBeforeOpen(): runs synchronously before the open animation (e.g. reset position)
 *  - onAfterOpen(): runs once the open animation settles (e.g. expand the chat)
 *
 * Returns { displayHidden, animStyle } for the host to apply to the container.
 */
export function useDockAnimation({
  isHidden,
  resolveTarget,
  resolveSource,
  onBeforeOpen,
  onAfterOpen
}) {
  // Seed from isHidden so a widget mounting already-hidden (persisted state)
  // stays hidden without playing the close animation.
  const [displayHidden, setDisplayHidden] = useState(isHidden);
  const [animStyle, setAnimStyle] = useState(null);
  const prevHiddenRef = useRef(isHidden);
  const timerRef = useRef(null);
  // Keep the latest callbacks without re-running the toggle effect on every render.
  const handlersRef = useRef({});
  handlersRef.current = { resolveTarget, resolveSource, onBeforeOpen, onAfterOpen };

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Fly from the resting spot into the navbar, fade out, then unmount visually.
  const playCloseAnimation = (dockedTransform) => {
    setDisplayHidden(false);
    setAnimStyle(atRest("none", 1));
    runAfterPaint(() => setAnimStyle(animatingTo(dockedTransform, 0)));
    timerRef.current = setTimeout(() => {
      setDisplayHidden(true);
      setAnimStyle(null);
    }, HIDE_DELAY_MS);
  };

  // Appear docked at the navbar, then fly out to the resting spot and fade in.
  const playOpenAnimation = (dockedTransform, onSettled) => {
    setDisplayHidden(false);
    setAnimStyle(atRest(dockedTransform, 0));
    runAfterPaint(() => setAnimStyle(animatingTo("none", 1)));
    timerRef.current = setTimeout(() => {
      setAnimStyle(null);
      onSettled?.();
    }, HIDE_DELAY_MS);
  };

  useEffect(() => {
    if (prevHiddenRef.current === isHidden) return;
    prevHiddenRef.current = isHidden;
    clearTimer();

    const { resolveTarget, resolveSource, onBeforeOpen, onAfterOpen } =
      handlersRef.current;

    if (isHidden) {
      const transform = computeDockTransform(resolveSource(true), resolveTarget());
      playCloseAnimation(transform);
    } else {
      onBeforeOpen?.();
      const transform = computeDockTransform(resolveSource(false), resolveTarget());
      playOpenAnimation(transform, onAfterOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHidden]);

  useEffect(() => clearTimer, []);

  return { displayHidden, animStyle };
}